"use client";

import Image from "next/image";
import { useRef, useState } from "react";
import { Blurhash } from "react-blurhash";
import { useFormContext } from "react-hook-form";
import { GrEdit } from "react-icons/gr";
import { PiUploadSimpleBold } from "react-icons/pi";
import {
    RiCheckboxCircleFill,
    RiImage2Line,
    RiImageAddLine,
} from "react-icons/ri";
import UploadImage from "~/components/file/UploadImage";
import { env } from "~/env.mjs";
import { TEMPORARY_BUCKET_KEY } from "~/utils/constants";
import { useEventDetailsPageStore } from "~/state/store";
import { EventImageType } from "~/types/EventTypes";
import { vibes } from "~/utils/cities";
import { cn } from "~/utils/fontsConfig";
import { getImagePath } from "~/utils/images";
import { If } from "../Conditional";
import { Button } from "../themed/Button";
import Modal from "../themed/Modal";
import { EventFormData } from "./EventViewAndEdit/EventFormDataType";

type EventImageOptions = {
    type?: "cover" | "contain";
};

type EventProp = EventImageType;
const ImageOverlayComponent = ({
    event,
    imageLoadingComplete,
    membershipName,
    className,
}: {
    event: EventProp;
    imageLoadingComplete: boolean;
    membershipName?: string;
    className?: string;
}) => {
    const [overlayLoadingComplete, setOverlayLoadingComplete] = useState(false);
    const [imgRef, setImgRef] = useState<HTMLImageElement | null>(null);

    const imageSrc = !!event?.user?.id
        ? `${env.NEXT_PUBLIC_ASSETS_URL}/profile/${event?.user?.id}/image-overlays/${event.imageOverlay?.brandAssetUrl}`
        : `${env.NEXT_PUBLIC_ASSETS_URL}/community/${event.community?.slug}/image-overlays/${event.imageOverlay?.brandAssetUrl}`;

    return (
        <If condition={!!event.imageOverlay?.brandAssetUrl}>
            <Image
                className={cn(
                    "aspect-video h-auto w-full rounded-[5px] transition-opacity duration-300 ease-in-out",
                    imageLoadingComplete ? "opacity-100" : "opacity-0",
                    className,
                )}
                fill
                src={imageSrc}
                priority
                sizes="(max-width: 768px) 100vw, 50vw"
                onLoad={(img) => {
                    setOverlayLoadingComplete(true);
                    setImgRef(img.currentTarget);
                }}
                alt="image-overlay"
            />
            {overlayLoadingComplete &&
                event.imageOverlay?.textsOverlay?.map((textOverlay, index) => {
                    let text = textOverlay.text;
                    if (textOverlay.type === "membership-name") {
                        if (!membershipName) return null;
                        text = membershipName;
                    } else if (textOverlay.type === "city-name") {
                        text = event.city;
                    }

                    if (!text) return null;
                    const x = textOverlay?.x ?? 0;
                    const y = textOverlay?.y ?? 0;

                    let fontScaler = 1;

                    if (imgRef && imgRef.width < 180) {
                        fontScaler = 0.7;
                    }

                    if (imgRef && imgRef.width > 600) {
                        fontScaler = 2;
                    }

                    let fontSize = textOverlay?.fontSize
                        ? `${textOverlay.fontSize * 10 * fontScaler}%`
                        : "100%";

                    return (
                        <div
                            className={cn(
                                "absolute bottom-0 left-0 z-[11] flex h-full w-full select-none items-center justify-center transition-opacity duration-300 ease-in-out",
                                className,
                            )}
                            key={text + index}
                        >
                            <div className="relative h-full w-full">
                                <div
                                    className="absolute leading-tight text-white"
                                    style={{
                                        fontSize,

                                        left: `${x}%`,
                                        top: `${y}%`,
                                    }}
                                >
                                    {text}
                                </div>
                            </div>
                        </div>
                    );
                })}
        </If>
    );
};

export const EventImage = ({
    event,
    className,
    options,
    isReadonly,
    allowAnimations = false,
    width,
    height,
    alt = "",
}: {
    event: EventProp;
    className?: string;
    options?: EventImageOptions;
    isReadonly?: boolean;
    allowAnimations?: boolean;
    width?: number;
    height?: number;
    alt?: string;
}) => {
    const form = isReadonly ? undefined : useFormContext<EventFormData>();
    const panelStore = isReadonly ? undefined : useEventDetailsPageStore();
    const [isUploading, setIsUploading] = useState(false);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);

    const [imageLoadingComplete, setImageLoadingComplete] = useState(false);
    const eventImage = form?.watch("image") || event.image;

    if (!event?.image) return null;

    return (
        <>
            {event.imageBlurhash && !imageLoadingComplete && (
                <div
                    className={cn(
                        "absolute h-full w-full rounded-[4px]",
                        options?.type === "contain" && "aspect-video",
                    )}
                >
                    <Blurhash
                        className="h-full w-full rounded-[4px]"
                        hash={event.imageBlurhash}
                        height="100%"
                        width="100%"
                        resolutionX={32}
                        resolutionY={32}
                        punch={1}
                    />
                </div>
            )}
            <div
                className={cn(
                    "relative h-full w-inherit",
                    allowAnimations && "transition-transform hover:scale-105",
                )}
            >
                {!options || options.type === "cover" ? (
                    <Image
                        width={width ?? 0}
                        height={height ?? 0}
                        className={cn("rounded-[4px]", className)}
                        fill={width || height ? false : true}
                        style={{ objectFit: "cover" }}
                        src={getImagePath(eventImage, event.id)}
                        priority
                        sizes="(max-width: 768px) 100vw, 50vw"
                        onLoad={() => setImageLoadingComplete(true)}
                        alt={alt}
                    />
                ) : (
                    <div className="relative">
                        <Image
                            width={width ?? 0}
                            height={height ?? 0}
                            className={cn(
                                "aspect-video h-auto w-full rounded-[4px]",
                                className,
                            )}
                            src={getImagePath(eventImage, event.id)}
                            priority
                            sizes="(max-width: 768px) 100vw, 50vw"
                            onLoad={() => setImageLoadingComplete(true)}
                            alt={alt}
                        />

                        <If
                            condition={
                                !isReadonly && panelStore?.panelMode === "edit"
                            }
                        >
                            <button
                                className={cn(
                                    "group absolute bottom-2 right-2",
                                    "z-50 cursor-pointer rounded-[0.6rem] bg-black/85 p-2",
                                    "transition-all ease-in hover:bg-black active:scale-95",
                                    {
                                        "cursor-not-allowed": isUploading,
                                    },
                                )}
                                onClick={(e) => setIsImageModalOpen(true)}
                                disabled={isUploading}
                            >
                                <GrEdit className="text-md text-white group-hover:text-v3-primary" />
                            </button>
                            <EventImageSelectionModal
                                isOpen={isImageModalOpen}
                                setIsOpen={setIsImageModalOpen}
                                eventId={event.id}
                                eventImage={event.image}
                            />
                        </If>
                    </div>
                )}
                <ImageOverlayComponent
                    event={event}
                    imageLoadingComplete={imageLoadingComplete}
                    membershipName={
                        event.community?.membershipTiers?.find((m) =>
                            m.tags?.some((t) => event.resourceTags.includes(t)),
                        )?.name
                    }
                    className={cn(
                        panelStore?.panelMode === "edit" && "opacity-30",
                    )}
                />
            </div>
        </>
    );
};

export const EventImageSelectionModal = ({
    isOpen,
    setIsOpen,
    eventId,
    eventImage,
    setIsUploading,
    className,
    overlayClassName,
}: {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    eventId?: string;
    eventImage?: string;
    setIsUploading?: (isUploading: boolean) => void;
    className?: string;
    overlayClassName?: string;
}) => {
    const form = useFormContext<EventFormData>();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploadedImageFilename, setUploadedImageFilename] = useState("");

    const [visibleCount, setVisibleCount] = useState(7);

    const originalImage = eventImage;
    const selectedImage = form.watch("image");

    const getImageURL = (
        image: {
            name: string | null;
            isRegularImage?: boolean;
        },
        fullPath = false,
    ) => {
        if (image.isRegularImage && fullPath)
            return getImagePath(image.name, eventId ?? TEMPORARY_BUCKET_KEY);
        else if (image.isRegularImage) return image.name;
        return `${env.NEXT_PUBLIC_ASSETS_URL}/city/vibes/${image.name}.webp`;
    };

    const visibleImages = [
        ...(originalImage
            ? [
                  {
                      name: originalImage,
                      blurHash: null,
                      isRegularImage: true,
                  },
              ]
            : []),
        ...vibes
            .filter((i) => originalImage !== getImageURL(i))
            .slice(0, visibleCount),
    ];

    const handleLoadMore = () => {
        setVisibleCount((prevCount) => prevCount + 6);
    };

    return (
        <Modal
            open={isOpen}
            onOpenChange={setIsOpen}
            className={cn("max-h-[70vh] overflow-scroll", className)}
            overlayClassName={overlayClassName}
            title="Update event image"
            description="Select from our library or upload your own."
            icon={<RiImage2Line className="text-[24px]" />}
            footer={
                <div className="mt-4 flex flex-row justify-end gap-2">
                    <Button
                        variant="secondary"
                        onClick={() => {
                            originalImage &&
                                form.setValue("image", originalImage);
                            setIsOpen(false);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        disabled={selectedImage === originalImage}
                        onClick={() => setIsOpen(false)}
                    >
                        Preview
                    </Button>
                </div>
            }
        >
            <div className="max-w-screen">
                <div className="flex flex-col justify-start gap-4 text-start">
                    <div className="text-left text-[16px] font-normal leading-normal">
                        <div className="flex flex-col gap-3">
                            <div className="mt-4">
                                <div className="grid grid-cols-3 gap-4">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (!uploadedImageFilename)
                                                return fileInputRef.current?.click();
                                            form.setValue(
                                                "image",
                                                uploadedImageFilename,
                                                { shouldDirty: true },
                                            );
                                        }}
                                        className={cn(
                                            `
                                                    transition-bg group relative
                                                    flex h-20
                                                    flex-col items-center
                                                    justify-center overflow-hidden rounded-lg
                                                    border-2 border-v3-O400
                                                    bg-v3-O500
                                                    text-xs duration-300
                                                    hover:bg-v3-O400
                                                    `,
                                            selectedImage ===
                                                uploadedImageFilename &&
                                                "border-v3-primary",
                                        )}
                                    >
                                        <RiImageAddLine
                                            className="transition-text h-6 w-6 text-white duration-300 group-hover:text-v3-primary"
                                            aria-hidden="true"
                                        />
                                        <div className="mt-2 flex flex-row items-center gap-2 px-2 text-xs text-v3-primary">
                                            <PiUploadSimpleBold
                                                className="hidden h-4 w-4 md:block"
                                                aria-hidden="true"
                                            />
                                            Upload custom
                                        </div>
                                        {uploadedImageFilename && (
                                            <Image
                                                fill
                                                src={getImagePath(
                                                    uploadedImageFilename,
                                                    eventId ??
                                                        TEMPORARY_BUCKET_KEY,
                                                )}
                                                alt="Event option"
                                                className="h-20 w-full object-cover"
                                            />
                                        )}
                                        {selectedImage ===
                                            uploadedImageFilename && (
                                            <RiCheckboxCircleFill className="absolute -top-0 right-0 text-v3-primary" />
                                        )}
                                    </button>
                                    <UploadImage
                                        className={cn(className, "hidden")}
                                        ref={fileInputRef}
                                        variant="button"
                                        bucketKey={
                                            eventId ?? TEMPORARY_BUCKET_KEY
                                        }
                                        cropBeforeUpload={true}
                                        onUploadComplete={(filename) => {
                                            setUploadedImageFilename(filename);
                                            form.setValue("image", filename, {
                                                shouldDirty: true,
                                            });
                                        }}
                                        setIsUploading={setIsUploading}
                                    />
                                    {visibleImages.map((image) => (
                                        <button
                                            key={image.name}
                                            type="button"
                                            onClick={() => {
                                                form.setValue(
                                                    "image",
                                                    getImageURL(image),
                                                    { shouldDirty: true },
                                                );
                                            }}
                                            className={cn(
                                                "relative h-20 overflow-hidden rounded-lg border-2",
                                                selectedImage ===
                                                    getImageURL(image)
                                                    ? "border-v3-primary"
                                                    : "border-gray-300",
                                            )}
                                        >
                                            <Image
                                                fill
                                                src={`${getImageURL(image, true)}`}
                                                alt="Event option"
                                                className="h-20 w-full object-cover"
                                            />

                                            {selectedImage ===
                                                getImageURL(image) && (
                                                <RiCheckboxCircleFill className="absolute -top-0 right-0 text-v3-primary" />
                                            )}
                                        </button>
                                    ))}
                                    {visibleCount < vibes.length && (
                                        <div className="flex items-end justify-end">
                                            <Button
                                                variant="link"
                                                onClick={handleLoadMore}
                                                className="p-0 px-1 text-sm hover:underline"
                                            >
                                                See More
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};
